import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import { type WorkOrder } from '@/types';
import { AlertCircle, CheckCircle, Clock, Loader2 } from 'lucide-react';
import { useState } from 'react';

interface CompleteWorkOrderDialogProps {
    workOrder: WorkOrder;
    isOpen: boolean;
    onClose: () => void;
    onComplete?: (data: { hours: number; minutes: number; notes?: string }) => void;
}

export function CompleteWorkOrderDialog({ workOrder, isOpen, onClose, onComplete }: CompleteWorkOrderDialogProps) {
    const { t } = useTranslations();
    const [hours, setHours] = useState('');
    const [minutes, setMinutes] = useState('');
    const [notes, setNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const hasAssignedWorkers = !!(workOrder.assigned_user || (workOrder.assignments && workOrder.assignments.length > 0));

        if (!hasAssignedWorkers) {
            return;
        }

        const hoursNum = parseFloat(hours) || 0;
        const minutesNum = parseInt(minutes) || 0;

        if (hoursNum === 0 && minutesNum === 0) {
            alert(t('work_orders.dialogs.complete.error_enter_time', 'Please enter at least some time spent'));
            return;
        }

        setIsSubmitting(true);

        // Simulate API call
        setTimeout(() => {
            onComplete?.({ hours: hoursNum, minutes: minutesNum, notes: notes.trim() || undefined });
            setIsSubmitting(false);
            setHours('');
            setMinutes('');
            setNotes('');
            onClose();
        }, 500);
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setHours('');
            setMinutes('');
            setNotes('');
            onClose();
        }
    };

    const totalTime = () => {
        const h = parseFloat(hours) || 0;
        const m = parseInt(minutes) || 0;
        if (h === 0 && m === 0) return '';
        if (m === 0) return `${h}h`;
        return `${h}h ${m}m`;
    };

    const assignedUser = workOrder.assigned_user || workOrder.assignments?.find((a) => a.role === 'lead')?.user;

    const hasAssignedWorkers = !!(workOrder.assigned_user || (workOrder.assignments && workOrder.assignments.length > 0));

    return (
        <ResponsiveModal open={isOpen} onOpenChange={handleClose} variant="sheet">
            <ResponsiveModalContent size="md">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle className="flex items-center gap-2">
                        <CheckCircle className="text-success-subtle-foreground h-5 w-5" />
                        {t('work_orders.dialogs.complete.title', 'Complete Work Order')}
                    </ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t('work_orders.dialogs.complete.description', 'Mark this work order as completed and track the time spent.')}
                        {assignedUser && (
                            <span className="mt-1 block font-medium">
                                {t('work_orders.dialogs.complete.time_will_be_recorded', 'The time will be recorded for the assigned worker:')}{' '}
                                {assignedUser.first_name} {assignedUser.last_name}
                            </span>
                        )}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
                    <ResponsiveModalBody className="space-y-4">
                        {!hasAssignedWorkers && (
                            <Alert variant="default">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription className="text-primary text-sm">
                                    This work order cannot be completed because no workers have been assigned. Please assign at least one worker
                                    before completing the work order.
                                </AlertDescription>
                            </Alert>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="hours">{t('work_orders.dialogs.complete.hours_spent', 'Hours Spent')} *</Label>
                                <Input
                                    id="hours"
                                    type="number"
                                    step="0.25"
                                    min="0"
                                    placeholder={t('work_orders.dialogs.complete.placeholder_hours', '0')}
                                    value={hours}
                                    onChange={(e) => setHours(e.target.value)}
                                    disabled={isSubmitting}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="minutes">{t('work_orders.dialogs.complete.additional_minutes', 'Additional Minutes')}</Label>
                                <Input
                                    id="minutes"
                                    type="number"
                                    min="0"
                                    max="59"
                                    placeholder={t('work_orders.dialogs.complete.placeholder_minutes', '0')}
                                    value={minutes}
                                    onChange={(e) => setMinutes(e.target.value)}
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        {totalTime() && (
                            <div className="bg-muted flex items-center gap-2 rounded-lg p-3">
                                <Clock className="text-muted-foreground h-4 w-4" />
                                <span className="text-sm font-medium">
                                    {t('work_orders.dialogs.complete.total_time', 'Total time:')} {totalTime()}
                                </span>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="notes">{t('work_orders.dialogs.complete.completion_notes', 'Completion Notes')}</Label>
                            <Textarea
                                id="notes"
                                placeholder={t('work_orders.dialogs.complete.placeholder_notes', 'Optional notes about the completion...')}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                disabled={isSubmitting}
                                rows={3}
                                maxLength={1000}
                            />
                            <p className="text-muted-foreground text-right text-xs">
                                {notes.length}/1000 {t('work_orders.dialogs.complete.characters', 'characters')}
                            </p>
                        </div>
                    </ResponsiveModalBody>

                    <ResponsiveModalFooter className="gap-2">
                        <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting} className="w-full sm:w-auto">
                            {t('work_orders.dialogs.complete.cancel', 'Cancel')}
                        </Button>
                        <Button
                            type="submit"
                            disabled={isSubmitting || (!hours && !minutes) || !hasAssignedWorkers}
                            className="bg-success text-success-foreground hover:bg-success/90 w-full sm:w-auto"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {t('work_orders.dialogs.complete.completing', 'Completing...')}
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="mr-2 h-4 w-4" />
                                    {t('work_orders.dialogs.complete.complete', 'Complete Work Order')}
                                </>
                            )}
                        </Button>
                    </ResponsiveModalFooter>
                </form>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
